/**
 * Discounts API Client
 */

import { apiClient } from "./client";
import type {
  Discount,
  StudentDiscount,
  CreateDiscountRequest,
  UpdateDiscountRequest,
  ApplyDiscountRequest,
  DiscountListParams,
  DiscountListResponse,
  StudentDiscountListResponse,
  DiscountDetailsResponse,
  ZakatFundSummary,
  ZakatTransferListResponse,
  ZakatTransfer,
  ZakatTransferDetails,
  RecordZakatTransferRequest,
} from "@/types/discount";
import type {
  FundingAward,
  GrantFundingAwardRequest,
  GrantFundingAwardResponse,
  UpdateFundingAwardRequest,
  RevokeFundingAwardRequest,
  SponsorSummary,
  OpenSponsorInvoice,
  RecordRemittanceRequest,
  SponsorRemittance,
} from "@/types/funding";

export const discountsApi = {
  // Discount Rules
  list: (params?: DiscountListParams) =>
    apiClient.get<DiscountListResponse>("/api/discounts/v1/rules", { params }),

  getById: (id: string) =>
    apiClient.get<DiscountDetailsResponse>(`/api/discounts/v1/rules/${id}`),

  create: (data: CreateDiscountRequest) =>
    apiClient.post<Discount>("/api/discounts/v1/rules", data),

  update: (id: string, data: UpdateDiscountRequest) =>
    apiClient.patch<Discount>(`/api/discounts/v1/rules/${id}`, data),

  delete: (id: string) => apiClient.delete(`/api/discounts/v1/rules/${id}`),

  // Student Discounts
  applyToStudent: (studentId: string, data: ApplyDiscountRequest) =>
    apiClient.post<StudentDiscount>(
      `/api/discounts/v1/students/${studentId}/apply`,
      data,
    ),

  removeFromStudent: (studentId: string, discountId: string) =>
    apiClient.delete(`/api/discounts/v1/students/${studentId}/${discountId}`),

  endStudentDiscount: (studentId: string, discountId: string) =>
    apiClient.post(`/api/discounts/v1/students/${studentId}/${discountId}/end`),

  // NOTE: These endpoints are not implemented in the backend yet.
  // Do not use until backend implementation is complete.
  // listStudentDiscounts: (params?: StudentDiscountListParams) =>
  //   apiClient.get<StudentDiscountListResponse>("/api/discounts/v1/students", { params }),

  // listPendingApprovals: (params?: StudentDiscountListParams) =>
  //   apiClient.get<StudentDiscountListResponse>("/api/discounts/v1/pending-approvals", { params }),

  // Get discounts applied to a specific student (this endpoint EXISTS)
  getStudentDiscounts: (studentId: string) =>
    apiClient.get<StudentDiscountListResponse>(
      `/api/discounts/v1/students/${studentId}/discounts`,
    ),

  getDiscountRecipients: (ruleId: string, status?: string) =>
    apiClient.get<{
      students: Array<{
        id: string;
        name: string;
        student_number: string;
        discount_applied_id: string;
        valid_from: string;
        valid_until: string | null;
        approved_by: string | null;
        approved_by_name: string | null;
        approved_at: string | null;
        approval_notes: string | null;
        // Set when the discount was granted for one class only.
        class_name?: string | null;
        is_currently_active: boolean;
      }>;
      total: number;
      active_count: number;
      expired_count: number;
    }>(`/api/discounts/v1/rules/${ruleId}/students`, {
      params: { status: status || "active" },
    }),

  /**
   * Export all discount rules (fetches all records for CSV export)
   */
  exportAll: async (params?: Omit<DiscountListParams, 'page' | 'limit'>) => {
    const allRules: Discount[] = [];
    let page = 1;
    const limit = 100;
    let hasMore = true;

    while (hasMore) {
      const response = await apiClient.get<DiscountListResponse>("/api/discounts/v1/rules", {
        params: { ...params, page, limit },
      });
      const data = response.data;
      allRules.push(...data.rules);

      hasMore = page < data.meta.total_pages;
      page++;
    }

    return { rules: allRules, meta: { total: allRules.length, page: 1, limit: allRules.length, total_pages: 1 } };
  },

  // Zakat Fund Tracking
  getZakatSummary: (params?: { start_date?: string; end_date?: string }) =>
    apiClient.get<ZakatFundSummary>("/api/discounts/v1/zakat/summary", { params }),

  getZakatTransferDetails: (transferId: string) =>
    apiClient.get<ZakatTransferDetails>(`/api/discounts/v1/zakat/transfers/${transferId}`),

  deleteZakatTransfer: (transferId: string) =>
    apiClient.delete(`/api/discounts/v1/zakat/transfers/${transferId}`),

  listZakatTransfers: (params?: { page?: number; limit?: number }) =>
    apiClient.get<ZakatTransferListResponse>("/api/discounts/v1/zakat/transfers", { params }),

  recordZakatTransfer: (data: RecordZakatTransferRequest) =>
    apiClient.post<ZakatTransfer>("/api/discounts/v1/zakat/transfers", data),

  // Sponsor Funding (TEFA, mig 163)
  grantFundingAward: (data: GrantFundingAwardRequest) =>
    apiClient.post<GrantFundingAwardResponse>(
      "/api/discounts/v1/funding-awards",
      data,
    ),

  listFundingAwards: (params?: {
    status?: string;
    period_label?: string;
    page?: number;
    limit?: number;
  }) =>
    apiClient.get<{
      awards: FundingAward[];
      meta: { page: number; limit: number; total: number; total_pages: number };
    }>("/api/discounts/v1/funding-awards", { params }),

  getFundingAward: (awardId: string) =>
    apiClient.get<FundingAward>(`/api/discounts/v1/funding-awards/${awardId}`),

  updateFundingAward: (awardId: string, data: UpdateFundingAwardRequest) =>
    apiClient.patch<FundingAward>(
      `/api/discounts/v1/funding-awards/${awardId}`,
      data,
    ),

  revokeFundingAward: (awardId: string, data: RevokeFundingAwardRequest) =>
    apiClient.post<FundingAward>(
      `/api/discounts/v1/funding-awards/${awardId}/revoke`,
      data,
    ),

  /** Idempotent heal for the award-committed-but-invoice-failed window */
  regenerateFundingInvoice: (awardId: string) =>
    apiClient.post<{ invoice: { id?: string; invoice_number?: string } }>(
      `/api/discounts/v1/funding-awards/${awardId}/regenerate-invoice`,
    ),

  getStudentFundingAwards: (studentId: string) =>
    apiClient.get<{ awards: FundingAward[] }>(
      `/api/discounts/v1/students/${studentId}/funding-awards`,
    ),

  getSponsorSummary: () =>
    apiClient.get<SponsorSummary>("/api/discounts/v1/sponsor/summary"),

  listOpenSponsorInvoices: () =>
    apiClient.get<{ invoices: OpenSponsorInvoice[] }>(
      "/api/discounts/v1/sponsor/open-invoices",
    ),

  listSponsorRemittances: (params?: { page?: number; limit?: number }) =>
    apiClient.get<{
      remittances: SponsorRemittance[];
      meta: { page: number; limit: number; total: number; total_pages: number };
    }>("/api/discounts/v1/sponsor/remittances", { params }),

  recordSponsorRemittance: (data: RecordRemittanceRequest) =>
    apiClient.post<SponsorRemittance>(
      "/api/discounts/v1/sponsor/remittances",
      data,
    ),

  getSponsorRemittance: (remittanceId: string) =>
    apiClient.get<SponsorRemittance>(
      `/api/discounts/v1/sponsor/remittances/${remittanceId}`,
    ),
};
